import { convexQuery, useConvexMutation } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';

interface SettingsFormValues {
  language: string;
  context: string;
  specialization: string;
  clinic: string;
}

const defaults: SettingsFormValues = {
  language: 'en',
  context: '',
  specialization: '',
  clinic: '',
};

export const useSettings = () => {
  const { i18n, t } = useTranslation();

  const { data: settings } = useQuery(
    convexQuery(api.userSettings.getSettings, {}),
  );

  const form = useForm<SettingsFormValues>({
    defaultValues: defaults,
  });

  // Sync server state → form when settings load or change
  useEffect(() => {
    if (!settings) return;

    const serverValues: SettingsFormValues = {
      language: settings.language || i18n.language.split('-')[0] || 'en',
      context: settings.context ?? '',
      specialization: settings.specialization ?? '',
      clinic: settings.clinic ?? '',
    };

    form.reset(serverValues);
    i18n.changeLanguage(serverValues.language);
  }, [settings, form, i18n]);

  const { mutate: updateSettings, isPending: isSaving } = useMutation({
    mutationFn: useConvexMutation(api.userSettings.updateSettings),
  });

  const handleSave = () => {
    const values = form.getValues();

    updateSettings(values, {
      onSuccess: () => {
        i18n.changeLanguage(values.language);
        toast.success(t('settings:saved', { lng: values.language }));
      },
      onError: () => {
        toast.error(t('settings:errorSaving', { lng: values.language }));
      },
    });
  };

  return {
    form,
    settings,
    language: form.watch('language') ?? 'en',
    isSaving,
    isDirty: form.formState.isDirty,
    handleSave,
  };
};
